/**
 * The colour themes behind Merchify's palette picker, lifted out so the
 * dashboard can offer the same set. Merchify still reads them from here, so
 * there is one list to add to rather than two to keep in step.
 */

// Shared node color palette — used by both the flowchart/mindmap renderer
// and the sequence-diagram renderer so participant boxes pick up the exact
// same brand swatches (and contrast logic) as flowchart nodes, by index.
export const NODE_PALETTE = [
  "rgb(92, 33, 12)",
  "rgb(122, 52, 24)",
  "rgb(79, 64, 58)",
  "rgb(201, 106, 60)",
  "rgb(63, 20, 8)",
  "rgb(77, 26, 10)",
  "rgb(42, 42, 42)",
  "rgb(56, 56, 56)",
  "rgb(70, 70, 70)",
  "rgb(84, 84, 84)",
];

// Every theme except "brand" (the AppFactor identity — kept exactly as-is)
// uses these same neutral, grayscale line colors: clearly visible against
// any background, but not tinted, so the lines don't compete with — or get
// confused as part of — the theme's actual (colored) node palette.
export const GRAYSCALE_EDGE = "#9ca3af";
export const GRAYSCALE_EDGE_HIGHLIGHT = "#e5e7eb";

// Selectable "style" (the toolbar's palette picker) — an accent/node-color
// theme layered independent of colorScheme (which only controls light/dark
// background+text). Each theme supplies its own node fill palette plus the
// edge/line and hover-highlight accent colors that go with it, so picking a
// theme recolors both nodes AND connecting lines together.
export const COLOR_THEMES = {
  brand: {
    label: "Appfactor",
    nodePalette: NODE_PALETTE,
    edge: "#f34f1c",
    edgeHighlight: "#ff8a5c",
  },
  // ─── The two that carry no hue at all ──────────────────────────────────
  // Every other theme answers "which category is this node" with a color.
  // These two answer it with a VALUE, which is what you want when the color
  // is already saying something else — a drawing where the thing being read
  // is the shape of the connections, or one going into a document or a slide
  // that has its own palette and does not want a second one arguing with it.
  //
  // Both are ramps rather than one repeated swatch: ten identical fills put
  // adjacent nodes into one blob with no edge between them, and the whole
  // job of a node palette is telling one node from the next. "White" is a
  // high-key ramp that still reads as white; "gray" runs the readable middle
  // of the scale, light enough at the bottom to hold up on a dark ground and
  // dark enough at the top to hold up on a light one.
  white: {
    label: "White",
    nodePalette: [
      "#ffffff", "#fafafa", "#f4f4f4", "#ededed", "#e6e6e6",
      "#dedede", "#d6d6d6", "#cecece", "#c6c6c6", "#bdbdbd",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  gray: {
    label: "Gray",
    nodePalette: [
      "#3d3d3d", "#4a4a4a", "#585858", "#666666", "#747474",
      "#828282", "#909090", "#9e9e9e", "#adadad", "#bbbbbb",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  // Every theme below except "brand" uses these same grayscale line colors
  // — only node fills carry the theme's actual hues; connecting lines stay
  // neutral (visible, not colorless-invisible) rather than tinted, per
  // explicit feedback. "brand" keeps its own colored lines unchanged.
  green: {
    label: "Green",
    nodePalette: [
      "#14532d", "#166534", "#15803d", "#16a34a", "#22c55e",
      "#065f46", "#047857", "#059669", "#10b981", "#34d399",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  blue: {
    label: "Blue",
    nodePalette: [
      "#1e3a8a", "#1e40af", "#1d4ed8", "#2563eb", "#3b82f6",
      "#0c4a6e", "#075985", "#0369a1", "#0284c7", "#0ea5e9",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  ocean: {
    label: "Ocean",
    nodePalette: [
      "#134e4a", "#115e59", "#0f766e", "#0d9488", "#14b8a6",
      "#164e63", "#155e75", "#0e7490", "#0891b2", "#06b6d4",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  chromatic: {
    label: "Chromatic",
    // A varied-hue palette (rather than a single-hue ramp) for a colorful,
    // "each node its own color" look.
    nodePalette: [
      "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6",
      "#3b82f6", "#6366f1", "#a855f7", "#ec4899", "#f43f5e",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  // ─── Widely-recognized editor/design color schemes ──────────────────────
  solarized: {
    label: "Solarized",
    nodePalette: [
      "#268bd2", "#2aa198", "#859900", "#b58900", "#cb4b16",
      "#dc322f", "#d33682", "#6c71c4", "#586e75", "#657b83",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  dracula: {
    label: "Dracula",
    nodePalette: [
      "#bd93f9", "#ff79c6", "#8be9fd", "#50fa7b", "#ffb86c",
      "#ff5555", "#f1fa8c", "#6272a4", "#44475a", "#9580ff",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  nord: {
    label: "Nord",
    nodePalette: [
      "#5e81ac", "#81a1c1", "#88c0d0", "#8fbcbb", "#a3be8c",
      "#ebcb8b", "#d08770", "#bf616a", "#b48ead", "#4c566a",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  monokai: {
    label: "Monokai",
    nodePalette: [
      "#f92672", "#a6e22e", "#fd971f", "#e6db74", "#66d9ef",
      "#ae81ff", "#75715e", "#a1efe4", "#f8f8f2", "#cc6633",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  gruvbox: {
    label: "Gruvbox",
    nodePalette: [
      "#cc241d", "#98971a", "#d79921", "#458588", "#b16286",
      "#689d6a", "#d65d0e", "#fb4934", "#fabd2f", "#8ec07c",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  onedark: {
    label: "One Dark",
    nodePalette: [
      "#61afef", "#98c379", "#e5c07b", "#e06c75", "#c678dd",
      "#56b6c2", "#d19a66", "#be5046", "#528bff", "#7f848e",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  tokyonight: {
    label: "Tokyo Night",
    nodePalette: [
      "#7aa2f7", "#bb9af7", "#f7768e", "#ff9e64", "#e0af68",
      "#9ece6a", "#73daca", "#7dcfff", "#c53b53", "#414868",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  catppuccin: {
    label: "Catppuccin",
    nodePalette: [
      "#cba6f7", "#f5c2e7", "#f38ba8", "#fab387", "#f9e2af",
      "#a6e3a1", "#94e2d5", "#89dceb", "#89b4fa", "#b4befe",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  github: {
    label: "GitHub",
    nodePalette: [
      "#0969da", "#1a7f37", "#8250df", "#cf222e", "#bc4c00",
      "#bf3989", "#9a6700", "#1b7c83", "#57606a", "#4b1fa7",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
  material: {
    label: "Material",
    nodePalette: [
      "#f44336", "#e91e63", "#9c27b0", "#673ab7", "#3f51b5",
      "#2196f3", "#009688", "#4caf50", "#ff9800", "#795548",
    ],
    edge: GRAYSCALE_EDGE,
    edgeHighlight: GRAYSCALE_EDGE_HIGHLIGHT,
  },
};
export const DEFAULT_COLOR_THEME = "brand";
